import { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus,
    faTrash,
    faEnvelope,
    faSpinner,
    faCheck,
    faTimes
} from '@fortawesome/free-solid-svg-icons';
import { emailAccountsService } from '../services/emailAccountsService';

export default function EmailAccountsManager({ onClose }) {
    const [accounts, setAccounts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newAccount, setNewAccount] = useState({ email: '', name: '' });
    const [creating, setCreating] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchAccounts();
    }, []);

    // Handle Telegram BackButton
    useEffect(() => {
        if (window.Telegram?.WebApp?.BackButton) {
            const handleBack = () => {
                if (showAddForm) {
                    setShowAddForm(false);
                } else {
                    onClose?.();
                }
            };
            window.Telegram.WebApp.BackButton.onClick(handleBack);
            window.Telegram.WebApp.BackButton.show();

            return () => {
                window.Telegram.WebApp.BackButton.offClick(handleBack);
                window.Telegram.WebApp.BackButton.hide();
            };
        }
    }, [showAddForm, onClose]);

    const fetchAccounts = async () => {
        try {
            setLoading(true);
            const data = await emailAccountsService.getAll();
            setAccounts(data);
        } catch (err) {
            console.error('Error fetching accounts:', err);
            setError('Не удалось загрузить аккаунты');
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setError(null);

        // Validate email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newAccount.email)) {
            setError('Неверный формат email');
            return;
        }

        // Check if email ends with @bobrhub.com
        if (!newAccount.email.endsWith('@bobrhub.com')) {
            setError('Email должен быть на домене @bobrhub.com');
            return;
        }

        try {
            setCreating(true);
            await emailAccountsService.create(newAccount.email, newAccount.name);
            setNewAccount({ email: '', name: '' });
            setShowAddForm(false);
            await fetchAccounts();
        } catch (err) {
            console.error('Error creating account:', err);
            setError(err.message || 'Ошибка при создании аккаунта');
        } finally {
            setCreating(false);
        }
    };

    const handleDelete = async (id, email) => {
        if (!confirm(`Удалить аккаунт ${email}?`)) {
            return;
        }

        try {
            await emailAccountsService.delete(id);
            await fetchAccounts();
        } catch (err) {
            console.error('Error deleting account:', err);
            alert('Ошибка при удалении аккаунта');
        }
    };

    const getInitials = (name) => {
        return name.charAt(0).toUpperCase();
    };

    const getAvatarColor = (email) => {
        const colors = [
            'from-blue-400 to-blue-600',
            'from-purple-400 to-purple-600',
            'from-pink-400 to-pink-600',
            'from-green-400 to-green-600',
            'from-yellow-400 to-yellow-600',
            'from-red-400 to-red-600',
        ];
        const index = (email || '').charCodeAt(0) % colors.length;
        return colors[index];
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full">
                <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-theme-button-color" />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full bg-theme-bg-color">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                <h2 className="text-lg font-semibold text-theme-text-color">Email Аккаунты</h2>
                {!showAddForm && (
                    <button
                        onClick={() => setShowAddForm(true)}
                        className="w-10 h-10 rounded-full bg-theme-button-color text-theme-button-text-color flex items-center justify-center shadow-lg"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                    </button>
                )}
            </div>

            {/* Add Form */}
            {showAddForm && (
                <div className="p-4 bg-theme-secondary-bg-color border-b border-theme-section-separator-color">
                    <form onSubmit={handleCreate} className="space-y-3">
                        <div>
                            <input
                                type="email"
                                placeholder="Email адрес (напр. support@bobrhub.com)"
                                value={newAccount.email}
                                onChange={(e) => setNewAccount({ ...newAccount, email: e.target.value })}
                                className="w-full px-4 py-3 bg-theme-bg-color text-theme-text-color rounded-lg border border-theme-section-separator-color focus:outline-none focus:ring-2 focus:ring-theme-button-color"
                                required
                            />
                        </div>
                        <div>
                            <input
                                type="text"
                                placeholder="Название (напр. Поддержка)"
                                value={newAccount.name}
                                onChange={(e) => setNewAccount({ ...newAccount, name: e.target.value })}
                                className="w-full px-4 py-3 bg-theme-bg-color text-theme-text-color rounded-lg border border-theme-section-separator-color focus:outline-none focus:ring-2 focus:ring-theme-button-color"
                                required
                            />
                        </div>

                        {error && (
                            <div className="text-red-500 text-sm">{error}</div>
                        )}

                        <div className="flex gap-2">
                            <button
                                type="submit"
                                disabled={creating}
                                className="flex-1 px-4 py-3 bg-theme-button-color text-theme-button-text-color rounded-lg font-medium disabled:opacity-50"
                            >
                                {creating ? (
                                    <FontAwesomeIcon icon={faSpinner} spin />
                                ) : (
                                    <><FontAwesomeIcon icon={faCheck} className="mr-2" />Создать</>
                                )}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowAddForm(false);
                                    setError(null);
                                    setNewAccount({ email: '', name: '' });
                                }}
                                className="px-4 py-3 bg-theme-hint-color/20 text-theme-hint-color rounded-lg font-medium"
                            >
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                    </form>
                </div>
            )}

            {/* Accounts List */}
            <div className="flex-1 overflow-y-auto">
                {accounts.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-theme-hint-color">
                        <FontAwesomeIcon icon={faEnvelope} className="text-5xl mb-4 opacity-50" />
                        <p>Нет email аккаунтов</p>
                        <p className="text-sm mt-2">Добавьте первый аккаунт</p>
                    </div>
                ) : (
                    <div className="divide-y divide-theme-section-separator-color">
                        {accounts.map((account) => (
                            <div
                                key={account.id}
                                className="flex items-center gap-3 px-4 py-4 hover:bg-theme-secondary-bg-color/50 transition-colors"
                            >
                                {/* Avatar */}
                                <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${getAvatarColor(account.email)} flex items-center justify-center text-white font-semibold text-lg shrink-0`}>
                                    {getInitials(account.name)}
                                </div>

                                {/* Info */}
                                <div className="flex-1 min-w-0">
                                    <div className="text-theme-text-color font-medium">
                                        {account.name}
                                    </div>
                                    <div className="text-theme-hint-color text-sm truncate">
                                        {account.email}
                                    </div>
                                    <div className="text-theme-hint-color text-xs mt-1">
                                        Папка: {account.folderName}
                                    </div>
                                </div>

                                {/* Actions */}
                                {account.id !== 'default' && (
                                    <button
                                        onClick={() => handleDelete(account.id, account.email)}
                                        className="w-10 h-10 rounded-full bg-red-500/20 text-red-500 flex items-center justify-center hover:bg-red-500/30 transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
